import { getUsersRetrieveQueryOptions, type UserResponse } from "@sidegigllc/api/client";
import type { QueryClient } from "@tanstack/react-query";
import { initializeAuth, sessionExists } from "domains/auth/initializeAuth";
import { AUTH_STALE_TIME, selectAuthUser } from "domains/auth/providers/Root/helpers";
import { adoptNativeSession } from "domains/auth/queries/nativeSession";

/**
 * Resolve the authenticated user inside a route loader, or `undefined` when signed out. Awaits the
 * "me" query so access checks are correct on a cold load / deep-link, where a synchronous cache read
 * races the auth root prefetch (CORE-5539); dedupes with that prefetch, so no extra request.
 *
 * Returns `undefined` (without fetching) when signed out so AuthProvider handles the login redirect
 * rather than the loader 401ing into the ErrorBoundary. Uses `hasSession` (not `doesSessionExist`
 * directly) for its automation bypass — see `initializeAuth`.
 */
export const loadAuthUser = async (queryClient: QueryClient): Promise<UserResponse | undefined> => {
  initializeAuth();
  await adoptNativeSession();
  if (!(await sessionExists())) return undefined;
  return selectAuthUser(
    await queryClient.query({
      ...getUsersRetrieveQueryOptions("me", { query: { staleTime: AUTH_STALE_TIME } }),
      staleTime: "static",
    }),
  );
};
